/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from "dayjs";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { FormSelect } from "../form/FormSelect";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { getWeekRange } from "../../services/dateService";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useDropLocations } from "../../store/useDropLocations";
import { IdCheckboxGrid } from "../form/IdCheckboxGrid";

interface Props {
  period: "all" | "yearly" | "quarterly" | "monthly" | "weekly";
  year: number;
  month: number;
  quarter: "Q1" | "Q2" | "Q3" | "Q4";
  week: Date;
  selectedFilters: string[];
  selectedStores?: string[];
  setPeriod: Dispatch<
    SetStateAction<"all" | "yearly" | "quarterly" | "monthly" | "weekly">
  >;
  setYear: Dispatch<SetStateAction<number>>;
  setMonth: Dispatch<SetStateAction<number>>;
  setQuarter: Dispatch<SetStateAction<"Q1" | "Q2" | "Q3" | "Q4">>;
  setWeek: Dispatch<SetStateAction<Date>>;
  setSelectedFilters: Dispatch<SetStateAction<string[]>>;
  setSelectedStores?: Dispatch<SetStateAction<string[]>>;
}

export const UserFilters = ({
  period = "all",
  year,
  month,
  quarter,
  week,
  selectedFilters,
  selectedStores,
  setPeriod,
  setYear,
  setMonth,
  setQuarter,
  setWeek,
  setSelectedFilters,
  setSelectedStores,
}: Props) => {
  /** Hooks */
  const { marketPrices } = useMarketPricing();
  const { dropLocations } = useDropLocations();
  /** Constants */
  const { startOfWeek, endOfWeek } = getWeekRange(week);

  /** Callbacks */
  const getLastWeek = () => setWeek(new Date(week.setDate(week.getDate() - 7)));
  const getNextWeek = () => setWeek(new Date(week.setDate(week.getDate() + 7)));

  const selectPeriod = (e: any) => setPeriod(e?.target?.value);
  const selectYear = (e: any) => setYear(e?.target?.value);
  const selectMonth = (e: any) => setMonth(e?.target?.value);
  const selectQuarter = (e: any) => setQuarter(e?.target?.value);

  const filterOptions = useMemo(() => {
    const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
    return [
      { id: "all", label: "All" },
      ...(kgOptions?.length
        ? kgOptions?.map((price) => ({
            id: price?._id as string,
            label: price?.name as string,
          }))
        : []),
      { id: "statiegeld", label: "Statiegeld" },
    ];
  }, [marketPrices]);

  const onSelectFilters = useCallback(
    (option: string) => {
      const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
      if (!setSelectedFilters || !selectedFilters) return;
      if (option === "all") {
        if (selectedFilters.includes("all")) {
          setSelectedFilters([]);
          return;
        }
        setSelectedFilters([
          "all",
          ...(kgOptions?.length
            ? kgOptions?.map((price) => price?._id as string)
            : []),
          "statiegeld",
        ]);
        return;
      }
      setSelectedFilters((prev: any) =>
        prev.includes(option)
          ? prev
              .filter((item: any) => item !== option)
              ?.filter((item: any) => item !== "all")
          : [...prev, option]?.filter((item: any) => item !== "all")
      );
    },
    [marketPrices, selectedFilters, filterOptions]
  );

  const onSelectStores = useCallback(
    (option: string) => {
      if (!setSelectedStores || !selectedStores) return;
      if (option === "all") {
        if (selectedStores.includes("all")) {
          setSelectedStores([]);
          return;
        }
        setSelectedStores([
          "all",
          ...(dropLocations?.length
            ? dropLocations?.map((location) => location?._id as string)
            : []),
        ]);
        return;
      }
      setSelectedStores((prev: any) =>
        prev.includes(option)
          ? prev
              .filter((item: any) => item !== option)
              ?.filter((item: any) => item !== "all")
          : [...prev, option]?.filter((item: any) => item !== "all")
      );
    },
    [dropLocations, selectedStores]
  );

  useEffect(() => {
    if (!setSelectedFilters) return;
    const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
    setSelectedFilters([
      "all",
      ...(kgOptions?.length
        ? kgOptions?.map((price) => price?._id as string)
        : []),
      "statiegeld",
    ]);
  }, [marketPrices]);

  useEffect(() => {
    if (!setSelectedStores) return;
    setSelectedStores([
      "all",
      ...(dropLocations?.map((location) => location?._id as string) ?? []),
    ]);
  }, [dropLocations]);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-4 sm:mb-8 mt-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col">
          <FormSelect
            label="Select period type"
            placeholder="Select period type"
            value={period ?? ""}
            // @ts-ignore
            options={
              ["All", "Yearly", "Quarterly", "Monthly", "Weekly"].map(
                (value) => ({
                  value: value?.toLowerCase(),
                  label: value,
                })
              ) ?? []
            }
            onChange={selectPeriod}
          />
          <div className="flex flex-row items-center">
            {period !== "all" && period !== "weekly" ? (
              <FormSelect
                label="Select period year"
                placeholder="Select period year"
                value={year}
                // @ts-ignore
                options={
                  Array.from(
                    { length: dayjs().year() - 2023 },
                    (_, i) => dayjs().year() - i
                  ).map((value) => ({
                    value,
                    label: value,
                  })) ?? []
                }
                onChange={selectYear}
              />
            ) : period !== "weekly" ? (
              <div className="opacity-0">
                <FormSelect
                  disabled
                  label="Select period year"
                  placeholder="Select period year"
                  value={year}
                  // @ts-ignore
                  options={
                    Array.from(
                      { length: dayjs().year() - 2023 },
                      (_, i) => dayjs().year() - i
                    ).map((value) => ({
                      value,
                      label: value,
                    })) ?? []
                  }
                  onChange={selectYear}
                />
              </div>
            ) : null}
            {period === "monthly" ? (
              <>
                <div className="ml-6" />
                <FormSelect
                  label="Select period month"
                  placeholder="Select period month"
                  value={month}
                  // @ts-ignore
                  options={
                    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((value) => ({
                      label: dayjs().month(value).format("MMMM"),
                      value,
                    })) ?? []
                  }
                  onChange={selectMonth}
                />
              </>
            ) : null}
            {period === "quarterly" ? (
              <>
                <div className="ml-6" />
                <FormSelect
                  label="Select period quarter"
                  placeholder="Select period quarter"
                  value={quarter}
                  // @ts-ignore
                  options={
                    ["Q1", "Q2", "Q3", "Q4"].map((value) => ({
                      label: value,
                      value,
                    })) ?? []
                  }
                  onChange={selectQuarter}
                />
              </>
            ) : null}
            {period === "weekly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0 max-w-[300px]">
                <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={endOfWeek > new Date()}
                  onClick={getNextWeek}
                  label="Next"
                />
              </div>
            ) : null}
          </div>
        </div>
        <IdCheckboxGrid
          label="Filter by Stream"
          options={filterOptions ?? []}
          selectedOptions={selectedFilters ?? []}
          onSelect={onSelectFilters}
          gridNumber={2}
        />
        <IdCheckboxGrid
          label="Select store to include in the stats"
          options={[
            { id: "all", label: "All" },
            ...(dropLocations
              ?.sort((a, b) => {
                return (b?.createdAt ?? new Date(0)) <
                  (a?.createdAt ?? new Date(0))
                  ? 1
                  : -1;
              })
              ?.map((location) => ({
                id: location?._id as string,
                label: location?.cost_place as string,
              })) ?? []),
          ]}
          gridNumber={2}
          selectedOptions={selectedStores ?? []}
          onSelect={onSelectStores}
        />
      </div>
    </div>
  );
};
